import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    Container,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { LockReset, Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { authAPI } from '../../services/api';
import './ResetPassword.css';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [userEmail, setUserEmail] = useState('');

    useEffect(() => {
        if (!token) {
            setError('Invalid reset link. Please request a new password reset.');
            setVerifying(false);
            return;
        }

        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        try {
            const response = await authAPI.verifyResetToken(token);
            setUserEmail(response.data.data.email);
            setVerifying(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid or expired reset link. Please request a new one.');
            setVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            await authAPI.resetPassword(token, newPassword);
            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <Box className="reset-password-container">
                <Container maxWidth="sm">
                    <Card elevation={3} sx={{ mt: 8, textAlign: 'center', p: 4 }}>
                        <CircularProgress size={60} sx={{ mb: 2 }} />
                        <Typography variant="h6">Verifying reset link...</Typography>
                    </Card>
                </Container>
            </Box>
        );
    }

    if (success) {
        return (
            <Box className="reset-password-container">
                <Container maxWidth="sm">
                    <Card elevation={3} sx={{ mt: 8 }}>
                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'success.main' }}>
                                Password Reset Successful!
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                Your password has been reset successfully. Redirecting to login...
                            </Typography>
                            <Button
                                component={Link}
                                to="/login"
                                variant="contained"
                                size="large"
                            >
                                Go to Login
                            </Button>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        );
    }

    return (
        <Box className="reset-password-container">
            <Container maxWidth="sm">
                <Card elevation={3} sx={{ mt: 8 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <LockReset sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                                Reset Password
                            </Typography>
                            {userEmail && (
                                <Typography variant="body2" color="text.secondary">
                                    Creating new password for: <strong>{userEmail}</strong>
                                </Typography>
                            )}
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {!error && (
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    sx={{ mb: 2 }}
                                    autoFocus
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="Confirm New Password"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    sx={{ mb: 3 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={loading}
                                    sx={{ mb: 2, py: 1.5 }}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                                </Button>

                                <Button
                                    component={Link}
                                    to="/login"
                                    variant="text"
                                    fullWidth
                                    sx={{ textTransform: 'none' }}
                                >
                                    Back to Login
                                </Button>
                            </form>
                        )}

                        {error && (
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Button
                                    component={Link}
                                    to="/forgot-password"
                                    variant="contained"
                                    fullWidth
                                >
                                    Request New Reset Link
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default ResetPassword;
